import React, { useMemo, useState } from "react";
import classnames from "classnames";
import "./hint.scss";

/**
 *
 * @param {String}              className - класс который можно модифицировать в SCSS
 * @param {String}              posX - (left, right, center) - позиция X относительно родителя где должна появлятся подсказка
 * @param {String}              posY - (top, bottom) - позиция Y относительно родителя где должна появлятся подсказка
 * @param {Object}              margin - объект, который принимает число, отступ, от родителя
 * @param {Object || String}    hintContent - JSX компонент или текст (окно всплывающей подсказки)
 * @param {Number}              maxWidth - ширина окна-подсказки
 * @param {Number}              edgeCut - отступ от краев экрана, если позиция подсказки превышает размеры экрана
 *
 */

export default function ({
  className,
  posX = "left",
  posY = "top",
  margin = {},
  children,
  hintContent: Content,
  maxWidth = 500,
  edgeCut = 0,
}) {
  const [ref, setRef] = useState(null);
  const [popupRef, setPopupRef] = useState(null);
  const popupWidth = useMemo(() => {
    return Math.min(maxWidth, window.innerWidth - edgeCut * 2);
  }, []);

  const [styles, setStyles] = useState({
    visibility: "hidden",
    opacity: 0,
    transition: "opacity .2s ease-in-out",
    width: popupWidth,
  });

  const HintContent = useMemo(
    (_) => {
      return typeof Content === "function" && React.isValidElement(<Content />)
        ? Content
        : () => <>{Content}</>;
    },
    [Content]
  );

  const mouseEnterHandler = (event) => {
    if (ref === null || popupRef === null) {
      return false;
    }

    let left = "auto",
      top = "auto";

    const parentCoords = ref.getBoundingClientRect();
    const popupCoords = popupRef.getBoundingClientRect();

    if (posX === "left") {
      left = parentCoords.left;

      const sum = left + popupCoords.width;

      if (sum > window.innerWidth) {
        left = left - (sum - window.innerWidth + edgeCut);
      }
    }
    if (posX === "right") {
      left = parentCoords.right - popupCoords.width;

      if (left < 0) {
        left = edgeCut;
      }
    }
    if (posX === "center") {
      left = parentCoords.left - popupCoords.width / 2;

      if (left < 0) {
        left = edgeCut;
      }
    }
    if (posY === "top") {
      top = parentCoords.top + parentCoords.height + (margin.top || 5);
    }
    if (posY === "bottom") {
      top =
        parentCoords.top -
        (parentCoords.height + popupCoords.height + (margin.bottom || 5));

      if (top < 0) {
        top = edgeCut;
      }
    }

    setStyles({
      left: left,
      top: top,
      visibility: "visible",
      opacity: 1,
      transition: "opacity .2s ease-in-out",
      width: popupWidth,
    });
  };

  const mouseLeaveHandler = (event) => {
    setStyles({
      visibility: "hidden",
      opacity: 0,
      transition: "opacity .2s ease-in-out",
      width: popupWidth,
    });
  };

  return (
    <div
      ref={setRef}
      className={classnames("hint", { [className]: !!className })}
      onMouseEnter={mouseEnterHandler}
      onMouseLeave={mouseLeaveHandler}
    >
      {children}
      <div ref={setPopupRef} className="hint__popup" style={styles}>
        <HintContent />
      </div>
    </div>
  );
}
